
import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://joesamenities.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: "Joe's Amenities",
  alternateName: 'Simple Amenities',
  url: SITE_URL,
  telephone: '+19089021076',
  email: 'simpleamenitiesllc@gmail.com',
  description:
    '100% free vending machines and coolers for New Jersey businesses. No cost, no maintenance, regularly restocked. Serving offices, gyms, and residential buildings across NJ.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Matawan',
    addressRegion: 'NJ',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 40.4168,
    longitude: -74.2299,
  },
  areaServed: {
    '@type': 'State',
    name: 'New Jersey',
  },
  priceRange: 'Free',
  sameAs: [
    'https://www.facebook.com/profile.php?id=61588557862981',
    'https://joesamenities.com/',
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday',
      ],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Free Vending Machine Placement',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Free Cooler Placement',
          description:
            'We place a free cooler at your business location at no cost to you.',
        },
        price: '0',
        priceCurrency: 'USD',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Free Vending Machine Placement',
          description:
            'We install and maintain a free vending machine at your location.',
        },
        price: '0',
        priceCurrency: 'USD',
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is the vending machine really free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — 100% free. There is no cost to you, ever. We provide the machine, stock it regularly, and handle all maintenance at zero charge.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who handles maintenance and restocking?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Joe's Amenities handles all maintenance and restocking. You never have to worry about repairs or running out of products.",
      },
    },
    {
      '@type': 'Question',
      name: 'What areas of New Jersey do you serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "We serve businesses across New Jersey including Matawan, Edison, Woodbridge, Freehold, Perth Amboy, Red Bank, and surrounding areas.",
      },
    },
    {
      '@type': 'Question',
      name: 'What types of businesses qualify for a free cooler?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We work with offices, fitness centers, residential buildings, warehouses, salons, and many other business types across New Jersey.',
      },
    },
  ],
};

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = ['schema-local-business', 'schema-faq'];
    const schemas = [localBusinessSchema, faqSchema];

    schemas.forEach((schema, i) => {
      const existing = document.getElementById(ids[i]);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = ids[i];
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1>
            <a href="/" className="flex items-center gap-3 cursor-pointer">
              <span className="text-xl font-bold text-white">SIMPLE AMENITIES</span>
            </a>
          </h1>

          <div className="flex items-center gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Contact
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started →
            </button>
          </div>
        </div>
      </nav>

      {/* Sections */}
      <Hero scrollToSection={scrollToSection} />
      <About />
      <Services />
      <Testimonials />
      <Contact />
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
}
